/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Grid, TextField, Typography } from "@mui/material";
import { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints } from "../../Utils/UrlEnum";
import { updateData } from "../../Services/updateData";
import { FormsIds } from "../../Utils/Constants";
import Config, { documentTypes } from "../../Utils/Config";
import { onFormError } from "../../Utils/Utils";
import { CRUDContext } from "./InstructorsTable";

interface UserInfoViewProps {
  onClose: () => void;
  setContextValue: (value: any) => void;
}

export default function UserInfoView(props: UserInfoViewProps) {
  const userContext: any = useContext(CRUDContext);

  const { register, control, handleSubmit } = useForm({
    mode: "onChange",
    defaultValues: {
      documentIds: Object.values(documentTypes).reduce((acc, type) => {
        const existing = userContext?.documentIds?.[type];
        acc[type] = {
          name: existing?.name ?? Vocabulary[type as keyof typeof Vocabulary] ?? type,
          expirationDate: existing?.expirationDate
            ? new Date(existing.expirationDate)
            : new Date(),
        };
        return acc;
      }, {} as Record<string, { name: string; expirationDate: Date }>),
    },
  });

  const onSubmit = async (model: any) => {
    const url = `${endpoints.user.main}/${userContext?._id}`;
    const res = (await updateData(url, { documentIds: model.documentIds })) as any;
    if (!res || res?.error) {
      return;
    }
    props.setContextValue([]);
    props.onClose();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      id={FormsIds.employeeDocumentsForm}
    >
      <Grid container spacing={Config.standardGridSpacing}>
        {/* Read-only identity fields */}
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            id="firstName"
            name="firstName"
            label={Vocabulary.firstName}
            value={userContext?.firstName || ""}
            fullWidth
            variant="outlined"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            id="lastName"
            name="lastName"
            label={Vocabulary.lastName}
            value={userContext?.lastName || ""}
            fullWidth
            variant="outlined"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            id="username"
            name="username"
            label={Vocabulary.username}
            value={userContext?.username || ""}
            fullWidth
            variant="outlined"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            id="email"
            name="email"
            label={Vocabulary.email}
            value={userContext?.email || ""}
            fullWidth
            variant="outlined"
            disabled
          />
        </Grid>

        {/* Document section */}
        <Typography
          variant="h5"
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
            width: "100%",
            padding: "15px",
            margin: "25px",
          }}
        >
          {Vocabulary.documents}
        </Typography>
        <Grid item xs={12} sm={12} md={12}>
          {Object.values(documentTypes).map((item, index) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={index}
              style={{ marginBottom: 20 }}
            >
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  label={
                    Vocabulary.documentName +
                    " (" +
                    Vocabulary[item as keyof typeof Vocabulary] +
                    ")"
                  }
                  {...register(
                    `documentIds.${
                      item as keyof typeof documentTypes
                    }.name` as const
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Controller
                      control={control}
                      name={
                        `documentIds.${
                          item as keyof typeof documentTypes
                        }.expirationDate` as const
                      }
                      render={({ field }) => (
                        <DatePicker
                          label={Vocabulary.documentDate}
                          value={field.value ? new Date(field.value) : null}
                          onChange={(newDate) => field.onChange(newDate)}
                          slots={{ textField: TextField }}
                          format="dd.MM.yyyy"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </form>
  );
}
